import React, { useEffect, useState } from "react";
import styled from "styled-components";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

/* ===== API base (inline, per your pattern) ===== */
const API_BASE =
  (typeof import.meta !== "undefined" &&
    import.meta.env &&
    import.meta.env.VITE_API_BASE_URL) ||
  process.env.REACT_APP_API_BASE_URL;

/* Minimal HTTP helper */
async function http(path, { method = "GET", data, headers, signal } = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: { "Content-Type": "application/json", ...(headers || {}) },
    body: data ? JSON.stringify(data) : undefined,
    signal,
    credentials: "include",
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.ok === false) {
    throw new Error(json?.error || `HTTP ${res.status}`);
  }
  return json;
}

/* Local API (page-only) */
const api = {
  listPublic: (params = {}) => {
    const usp = new URLSearchParams(params);
    const qs = usp.toString() ? `?${usp.toString()}` : "";
    return http(`/api/blogs/public${qs}`);
  },
  suggestTopic: (payload) => http(`/api/blog-topics`, { method: "POST", data: payload }),
};

const imgSrc = (fileName) => `${API_BASE}/uploads/blogs/${fileName}`;

/* ===== THEME / STYLES ===== */
const theme = {
  brand: "#3dccc7",
  brandDark: "#2a5a7a",
  accent: "#ff914d",
  text: "#0b1320",
  white: "#f1f1f1",
  subtext: "#6b7280",
  card: "#ffffff",
  border: "#e5e7eb",
  bg: "#f8fafc",
};

const PageShell = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100dvh;
  background: ${theme.bg};
`;

const Main = styled.main`
  flex: 1 0 auto;
  padding-top: 80px;
  @media (max-width: 768px) {
    padding-top: 70px;
  }
  @media (max-width: 480px) {
    padding-top: 60px;
  }
`;

const Header = styled.section`
  background: linear-gradient(135deg, ${theme.brand} 0%, ${theme.brandDark} 100%);
  color: #fff;
  text-align: center;
  padding: clamp(2.4rem, 7vw, 3rem) 1rem;
`;
const H1 = styled.h1`
  margin: 0.75rem 0 0.6rem;
  font-weight: 900;
  letter-spacing: 0.2px;
  line-height: 1.12;
  font-size: clamp(1.8rem, 5.8vw, 3rem);
`;
const Sub = styled.p`
  margin: 0.85rem auto 0;
  opacity: 0.98;
  font-size: clamp(0.95rem, 2.4vw, 1.1rem);
  max-width: 880px;
`;

const Wrap = styled.div`
  display: grid;
  grid-template-columns: 340px 1fr;
  gap: 32px;
  max-width: min(1400px, 96vw);
  margin: 32px auto;
  padding: 0 16px;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;
const Sidebar = styled.aside`
  position: sticky;
  top: 90px;
  align-self: start;
  display: grid;
  gap: 12px;

  @media (max-width: 980px) {
    position: static;
  }
`;

const Card = styled.div`
  background: ${theme.card};
  border: 1px solid ${theme.border};
  border-radius: 16px;
  padding: 14px;
`;
const List = styled.ol`
  list-style: none;
  counter-reset: postcount;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 6px;
`;
const ListItem = styled.li`
  counter-increment: postcount;
  display: grid;
  grid-template-columns: 28px 1fr;
  align-items: center;
  gap: 8px;
  a {
    display: block;
    padding: 8px 10px;
    border-radius: 10px;
    text-decoration: none;
    color: ${theme.text};
    border: 1px dashed transparent;
  }
  a:hover {
    background: #f3f4f6;
    border-color: ${theme.border};
  }
  &::before {
    content: counter(postcount) ".";
    font-weight: 800;
    color: ${theme.brandDark};
    text-align: right;
  }
`;

const Help = styled.div`
  margin-top: 12px;
  background: #0f1b2d;
  color: ${theme.white};
  border-radius: 14px;
  padding: 14px;
  border: 1px solid #1e293b;
  small {
    opacity: 0.85;
  }
`;

const TopicForm = styled.form`
  display: grid;
  gap: 8px;
  margin-top: 10px;
`;
const TopicTextarea = styled.textarea`
  width: 100%;
  min-height: 70px;
  padding: 10px;
  border-radius: 10px;
  border: 1px solid #1e293b;
  background: #0b1320;
  color: ${theme.white};
  resize: vertical;
  font-family: inherit;
  font-size: 0.9rem;
  &::placeholder {
    color: #6b7280;
  }
`;
const TopicSubmit = styled.button`
  justify-self: start;
  background: linear-gradient(45deg, ${theme.accent}, #ff6b35);
  color: #fff;
  border: 0;
  padding: 8px 16px;
  border-radius: 999px;
  font-weight: 700;
  font-size: 0.85rem;
  cursor: pointer;
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;
const TopicNote = styled.small`
  color: ${(p) => (p.$error ? "#fca5a5" : "#86efac")};
  display: block;
`;

const Posts = styled.section`
  display: grid;
  gap: 32px;
  @media (max-width: 600px) {
    gap: 18px;
  }
`;
const Post = styled.article`
  background: ${theme.card};
  border: 1px solid ${theme.border};
  border-radius: 18px;
  padding: clamp(16px, 2vw, 24px);
  box-shadow: 0 12px 32px rgba(16, 24, 40, 0.06);
`;
const Title = styled.h2`
  margin: 0 0 6px;
  letter-spacing: 0.2px;
  font-size: clamp(1.1rem, 2.8vw, 1.4rem);
`;
const Meta = styled.div`
  font-size: 14px;
  color: ${theme.subtext};
`;
const Sep = styled.hr`
  border: 0;
  border-top: 1px dashed ${theme.border};
  margin: 16px 0;
`;
const Content = styled.div`
  line-height: 1.7;
  color: ${theme.text};
  white-space: pre-wrap;
  font-size: clamp(0.92rem, 1.9vw, 0.95rem);
`;

const Gallery = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 10px;
`;
const Thumb = styled.button`
  padding: 0;
  border: 1px solid ${theme.border};
  border-radius: 12px;
  overflow: hidden;
  cursor: zoom-in;
  background: #f3f4f6;
  aspect-ratio: 4 / 3;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const LinksList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;
const LinkChip = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 12px;
  border-radius: 999px;
  font-weight: 700;
  font-size: 0.85rem;
  text-decoration: none;
  background: linear-gradient(45deg, ${theme.accent}, #ff6b35);
  color: #fff;
  box-shadow: 0 6px 18px rgba(255, 145, 77, 0.25);
  &:hover {
    transform: translateY(-1px);
  }
`;

/* ===== Lightbox ===== */
const LightboxOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(10, 15, 25, 0.85);
  display: grid;
  place-items: center;
  z-index: 9999;
  padding: 24px;
`;
const LightboxImg = styled.img`
  max-width: min(92vw, 1000px);
  max-height: 86vh;
  border-radius: 10px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
`;
const LightboxBtn = styled.button`
  position: absolute;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.35);
  color: #fff;
  border-radius: 999px;
  width: 42px;
  height: 42px;
  font-size: 1.2rem;
  cursor: pointer;
  display: grid;
  place-items: center;
  &:hover {
    background: rgba(255, 255, 255, 0.25);
  }
`;
const CloseBtn = styled(LightboxBtn)`
  top: 20px;
  right: 20px;
`;
const PrevBtn = styled(LightboxBtn)`
  left: 20px;
  top: 50%;
  transform: translateY(-50%);
`;
const NextBtn = styled(LightboxBtn)`
  right: 20px;
  top: 50%;
  transform: translateY(-50%);
`;

export default function Blog() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null); // { images, index }

  const [topicText, setTopicText] = useState("");
  const [topicSending, setTopicSending] = useState(false);
  const [topicNote, setTopicNote] = useState(null); // { error, message }

  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        setLoading(true);
        const res = await api.listPublic({});
        if (!ignore) setItems(res.data || []);
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => {
      ignore = true;
    };
  }, []);

  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(null);
      if (e.key === "ArrowRight") stepLightbox(1);
      if (e.key === "ArrowLeft") stepLightbox(-1);
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lightbox]);

  function stepLightbox(dir) {
    setLightbox((lb) => {
      if (!lb) return lb;
      const len = lb.images.length;
      const index = (lb.index + dir + len) % len;
      return { ...lb, index };
    });
  }

  async function handleTopicSubmit(e) {
    e.preventDefault();
    if (!topicText.trim()) return;
    setTopicSending(true);
    setTopicNote(null);
    try {
      await api.suggestTopic({ topic: topicText.trim() });
      setTopicText("");
      setTopicNote({ error: false, message: "Thanks! We'll consider it for an upcoming post." });
    } catch (err) {
      setTopicNote({ error: true, message: err.message || "Couldn't submit right now. Please try again." });
    } finally {
      setTopicSending(false);
    }
  }

  const fmt = (d) =>
    d
      ? new Date(d).toLocaleDateString(undefined, {
          year: "numeric",
          month: "short",
          day: "numeric",
        })
      : "";

  const scrollToId = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    const title = el.querySelector("h2") || el;
    let yOffset = -120;
    if (window.innerWidth <= 480) yOffset = -80;
    else if (window.innerWidth <= 768) yOffset = -100;
    const y = title.getBoundingClientRect().top + window.scrollY + yOffset;
    window.scrollTo({ top: y, behavior: "smooth" });
  };

  return (
    <PageShell>
      <Navbar />
      <Main>
        <Header>
          <H1>Blogs</H1>
          <Sub>
            Practical stories, tips and updates on using AI in construction.
          </Sub>
        </Header>
        <Wrap>
          <Sidebar>
            <Card>
              <strong style={{ display: "block", marginBottom: 8 }}>
                All posts
              </strong>
              <List>
                {items.map((p) => (
                  <ListItem key={p._id}>
                    <a
                      href={`#${p.slug}`}
                      onClick={(e) => {
                        e.preventDefault();
                        scrollToId(p.slug);
                      }}
                    >
                      {p.title}
                    </a>
                  </ListItem>
                ))}
              </List>
            </Card>
            <Help>
              <strong>Have a topic you want covered?</strong>
              <TopicForm onSubmit={handleTopicSubmit}>
                <TopicTextarea
                  value={topicText}
                  onChange={(e) => setTopicText(e.target.value)}
                  placeholder="Type your topic idea..."
                  maxLength={1000}
                  disabled={topicSending}
                  required
                />
                <TopicSubmit type="submit" disabled={topicSending || !topicText.trim()}>
                  {topicSending ? "Sending..." : "Submit idea"}
                </TopicSubmit>
                {topicNote && <TopicNote $error={topicNote.error}>{topicNote.message}</TopicNote>}
              </TopicForm>
            </Help>
          </Sidebar>

          <Posts>
            {loading && <Card>Loading posts…</Card>}
            {!loading && items.length === 0 && <Card>No posts yet.</Card>}
            {!loading &&
              items.map((p) => (
                <Post id={p.slug} key={p._id}>
                  <Title>{p.title}</Title>
                  <Meta>By {p.author || "Abhishek Shete"} • Posted {fmt(p.postedAt)}</Meta>
                  <Sep />

                  {Array.isArray(p.images) && p.images.length > 0 && (
                    <>
                      <Gallery>
                        {p.images.map((img, i) => (
                          <Thumb
                            key={img.fileName}
                            onClick={() => setLightbox({ images: p.images, index: i })}
                            aria-label={`Open image ${i + 1}`}
                          >
                            <img src={imgSrc(img.fileName)} alt={img.originalName || p.title} loading="lazy" />
                          </Thumb>
                        ))}
                      </Gallery>
                      <Sep />
                    </>
                  )}

                  <Content>{p.content}</Content>

                  {Array.isArray(p.links) && p.links.length > 0 && (
                    <>
                      <Sep />
                      <LinksList>
                        {p.links.map((l, i) => (
                          <LinkChip key={i} href={l.url} target="_blank" rel="noopener noreferrer">
                            🔗 {l.label || l.url}
                          </LinkChip>
                        ))}
                      </LinksList>
                    </>
                  )}
                </Post>
              ))}
          </Posts>
        </Wrap>
      </Main>
      <Footer />

      {lightbox && (
        <LightboxOverlay
          role="dialog"
          aria-modal="true"
          onClick={() => setLightbox(null)}
        >
          <CloseBtn onClick={() => setLightbox(null)} aria-label="Close">✕</CloseBtn>
          {lightbox.images.length > 1 && (
            <>
              <PrevBtn
                onClick={(e) => {
                  e.stopPropagation();
                  stepLightbox(-1);
                }}
                aria-label="Previous image"
              >
                ‹
              </PrevBtn>
              <NextBtn
                onClick={(e) => {
                  e.stopPropagation();
                  stepLightbox(1);
                }}
                aria-label="Next image"
              >
                ›
              </NextBtn>
            </>
          )}
          <LightboxImg
            src={imgSrc(lightbox.images[lightbox.index].fileName)}
            alt=""
            onClick={(e) => e.stopPropagation()}
          />
        </LightboxOverlay>
      )}
    </PageShell>
  );
}
